import { isLocalhostDevMode } from "@/utils/utils";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { syncAcrossTabs } from "../../app/store/syncAcrossTabs";
import type { UUID } from "../../typings/global";
import type { AuthenticatedLoginResponse, AuthSelectedAccount, AuthToken } from "./types";
import { isAuthTokenValid, resolveSelectedAccount } from "./utils";

interface AuthSnapshot {
  session?: AuthToken;
  selectedAccount: AuthSelectedAccount;
  rememberMe?: AuthToken;
}

interface AuthState extends AuthSnapshot {
  actions: {
    authenticated: (response: AuthenticatedLoginResponse) => void;
    tokenRefreshed: (refreshedToken: string | undefined, response: AuthenticatedLoginResponse) => void;
    accountSwitched: (accountId: UUID) => void;
    sessionEnded: () => void;
  };
}

/**
 * The client side of a session - the token and the account it acts for; the user behind them lives in the session
 * query. The selected account and the remember-me token outlive a logout, so the next login returns to the same
 * account and, while the remember-me token is valid, skips the TOTP code.
 */
const useAuthStore = create<AuthState>()(
  devtools(
    syncAcrossTabs(
      persist(
        (set) => ({
          session: undefined,
          selectedAccount: {},
          rememberMe: undefined,
          actions: {
            authenticated: (response) =>
              set((state) => ({
                session: { token: response.token, validUntil: response.tokenValidUntil },
                selectedAccount: resolveSelectedAccount(response.user, state.selectedAccount),
                rememberMe:
                  response.rememberMeToken && response.rememberMeTokenValidUntil
                    ? { token: response.rememberMeToken, validUntil: response.rememberMeTokenValidUntil }
                    : state.rememberMe,
              })),
            // A refresh answered after the session ended or was replaced by a new login must leave it alone.
            tokenRefreshed: (refreshedToken, response) =>
              set((state) =>
                state.session && state.session.token === refreshedToken
                  ? { session: { token: response.token, validUntil: response.tokenValidUntil } }
                  : state,
              ),
            accountSwitched: (accountId) =>
              set((state) => ({ selectedAccount: { ...state.selectedAccount, accountId } })),
            sessionEnded: () => set({ session: undefined }),
          },
        }),
        {
          name: "cliip:auth",
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            session: state.session,
            selectedAccount: state.selectedAccount,
            rememberMe: state.rememberMe,
          }),
        },
      ),
    ),
    { name: "auth", enabled: isLocalhostDevMode() },
  ),
);

export const subscribeToAuth = (listener: (state: AuthSnapshot, previous: AuthSnapshot) => void) =>
  useAuthStore.subscribe(listener);

export const useIsAuthenticated = () => useAuthStore((state) => isAuthTokenValid(state.session));

export const useAuthToken = () => useAuthStore((state) => state.session?.token);

// A rehydrate parses a new object even when another tab changed only the token.
export const useSelectedAccount = () => useAuthStore(useShallow((state) => state.selectedAccount));

export const getAuthSnapshot = (): AuthSnapshot => useAuthStore.getState();

export const authActions = () => useAuthStore.getState().actions;
